// Web Audio API chime synthesizer for Kitchen & Order alerts

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    audioCtx = new AudioContextClass();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

export type OrderSoundType = 'cash_register' | 'service_bell' | 'marimba_chord' | 'subtle_swoosh';

/**
 * 1. Apple Pay / Modern Cashier Success Chime (Crisp double high-tone ascending)
 */
export function playModernSuccessChime() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = 'sine';
    osc2.type = 'sine';

    // Ascending pure tone (C6 -> G6)
    osc1.frequency.setValueAtTime(1046.50, now);
    osc1.frequency.setValueAtTime(1567.98, now + 0.12);

    osc2.frequency.setValueAtTime(523.25, now);
    osc2.frequency.setValueAtTime(783.99, now + 0.12);

    gain.gain.setValueAtTime(0.35, now);
    gain.gain.setValueAtTime(0.4, now + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.75);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.75);
    osc2.stop(now + 0.75);
  } catch (e) {
    console.warn('Audio suppressed:', e);
  }
}

/**
 * 2. Classic Restaurant Service Bell (Clear brass chime)
 */
export function playServiceBellChime() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(1760, now); // A6 bell ring
    osc.frequency.exponentialRampToValueAtTime(1740, now + 0.5);

    gain.gain.setValueAtTime(0.45, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.9);
  } catch (e) {
    console.warn('Audio suppressed:', e);
  }
}

/**
 * 3. Warm Nordic Marimba Chord (Soft melodic arpeggio)
 */
export function playMarimbaChord() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = 'sine';
    osc2.type = 'triangle';

    osc1.frequency.setValueAtTime(659.25, now);
    osc1.frequency.exponentialRampToValueAtTime(830.61, now + 0.15);
    osc1.frequency.exponentialRampToValueAtTime(987.77, now + 0.35);

    osc2.frequency.setValueAtTime(329.63, now);
    osc2.frequency.exponentialRampToValueAtTime(493.88, now + 0.2);

    gain.gain.setValueAtTime(0.35, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.8);
    osc2.stop(now + 0.8);
  } catch (e) {
    console.warn('Audio suppressed:', e);
  }
}

/**
 * 4. Futuristic Smooth Swoosh & Confirmation
 */
export function playSwooshConfirm() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.2);

    gain.gain.setValueAtTime(0.01, now);
    gain.gain.linearRampToValueAtTime(0.35, now + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.6);
  } catch (e) {
    console.warn('Audio suppressed:', e);
  }
}

// Master dispatch function based on saved preference
export function playNewOrderChime(soundType: OrderSoundType = 'cash_register') {
  switch (soundType) {
    case 'cash_register':
      playModernSuccessChime();
      break;
    case 'service_bell':
      playServiceBellChime();
      break;
    case 'marimba_chord':
      playMarimbaChord();
      break;
    case 'subtle_swoosh':
      playSwooshConfirm();
      break;
    default:
      playModernSuccessChime();
  }
}

// Gentle attention bell for order change request
export function playChangeRequestAlert() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now); // A5
    osc.frequency.setValueAtTime(587.33, now + 0.15); // D5
    osc.frequency.setValueAtTime(880, now + 0.3); // A5

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.6);
  } catch (e) {
    console.warn('Audio change alert error:', e);
  }
}

/**
 * Cheerful Celebration Chime when food is served (C5 -> E5 -> G5 -> C6 joy sparkle)
 */
export function playServedCelebrationSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const frequencies = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    frequencies.forEach((freq, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + index * 0.1);

      gain.gain.setValueAtTime(0, now + index * 0.1);
      gain.gain.linearRampToValueAtTime(0.3, now + index * 0.1 + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + index * 0.1 + 0.7);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + index * 0.1);
      osc.stop(now + index * 0.1 + 0.7);
    });
  } catch (e) {
    console.warn('Audio celebration suppressed:', e);
  }
}

